import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  Loader2,
  Mail,
  Phone,
  Copy,
  ExternalLink,
  FileText,
  Search,
  Trash2,
  MessageSquare,
  Send,
  Activity,
  CalendarClock,
  Video,
  MapPin,
  Plus,
  Link as LinkIcon,
  CheckCircle2,
  XCircle,
  UserPlus,
  IdCard,
  RefreshCcw,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { DateTimeInput } from "@/components/ui/datetime-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NumericInput } from "@/components/ui/numeric-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { STATUS_TONES, STATUS_LABELS } from "./Recruitment";

const STATUS_ORDER = [
  "new",
  "contacted",
  "interview",
  "waiting",
  "rejected",
  "hired",
];

const formatDateHu = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("hu-HU", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return s;
  }
};

export default function PositionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [posRes, appsRes] = await Promise.all([
        api.get(`/positions/${id}`),
        api.get(`/positions/${id}/applications`),
      ]);
      setPosition(posRes.data);
      setApplications(appsRes.data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a pozíciót."
      );
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh: csak az applications listát frissíti, nincs loader.
  const refreshApplications = async () => {
    try {
      const { data } = await api.get(`/positions/${id}/applications`);
      setApplications(data);
      window.dispatchEvent(new CustomEvent("recruitment:changed"));
    } catch {
      // silent — auto-polling, ne zavarjuk a felhasználót
    }
  };

  useEffect(() => {
    fetchAll();
    // Auto-polling 2 percenként, így az új jelentkezések rövid idő után láthatók.
    const interval = setInterval(refreshApplications, 120000);
    // Tab/ablak fókuszra azonnal frissítünk.
    const onFocus = () => refreshApplications();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const counts = useMemo(() => {
    const c = { all: applications.length };
    STATUS_ORDER.forEach((s) => {
      c[s] = applications.filter((a) => a.status === s).length;
    });
    return c;
  }, [applications]);

  const visible = useMemo(() => {
    let list = applications;
    if (filter !== "all") list = list.filter((a) => a.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (a) =>
          a.last_name.toLowerCase().includes(q) ||
          a.first_name.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          (a.phone && a.phone.toLowerCase().includes(q))
      );
    }
    return list;
  }, [applications, filter, search]);

  const onStatusChange = async (application, newStatus) => {
    if (newStatus === application.status) return;
    try {
      const { data } = await api.patch(
        `/applications/${application._id}/status`,
        { status: newStatus }
      );
      setApplications((prev) =>
        prev.map((a) => (a._id === application._id ? data : a))
      );
      setSelected((curr) => (curr && curr._id === application._id ? data : curr));
      toast.success(`Státusz módosítva: ${STATUS_LABELS[newStatus]}`);
      // Tell the sidebar to refresh its "new applications" badge.
      window.dispatchEvent(new CustomEvent("recruitment:changed"));
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült a státuszt módosítani."
      );
    }
  };

  const onDeleteApp = async (application) => {
    if (
      !window.confirm(
        `Biztosan törlöd ${application.last_name} ${application.first_name} jelentkezését?`
      )
    )
      return;
    try {
      await api.delete(`/applications/${application._id}`);
      setApplications((prev) => prev.filter((a) => a._id !== application._id));
      setSelected(null);
      toast.success("Jelentkezés törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  const updateApplication = (appId, updater) => {
    setApplications((prev) =>
      prev.map((a) => (a._id === appId ? updater(a) : a))
    );
    setSelected((curr) => (curr && curr._id === appId ? updater(curr) : curr));
  };

  const copyPublicLink = () => {
    if (!position) return;
    const url = `${window.location.origin}/apply/${position.slug}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success("Link a vágólapra másolva."))
      .catch(() => toast.error("Nem sikerült másolni."));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }
  if (!position) return null;

  return (
    <div data-testid="position-detail-page" className="space-y-7">
      <button
        onClick={() => navigate("/recruitment")}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Toborzás
      </button>

      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            <Briefcase className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
              Pozíció
            </span>
            {!position.active && (
              <span className="ml-2 rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                inaktív
              </span>
            )}
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-tight">
            {position.name}
          </h1>
          {position.location && (
            <p className="mt-1 text-sm text-muted-foreground">{position.location}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              await refreshApplications();
              toast.success("Jelentkezések frissítve.");
            }}
            data-testid="refresh-applications"
          >
            <RefreshCcw className="h-4 w-4" /> Frissítés
          </Button>
          <Button variant="outline" onClick={copyPublicLink} data-testid="copy-link">
            <Copy className="h-4 w-4" /> Link másolása
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/apply/${position.slug}`, "_blank")}
            data-testid="open-public-form"
          >
            <ExternalLink className="h-4 w-4" /> Űrlap megnyitása
          </Button>
        </div>
      </header>

      <Tabs defaultValue="applications" className="space-y-4">
        <TabsList data-testid="position-tabs" className="h-10 bg-foreground/[0.04]">
          <TabsTrigger value="applications" data-testid="tab-applications" className="px-4">
            Jelentkezések
          </TabsTrigger>
          <TabsTrigger value="interviews" data-testid="tab-interviews" className="px-4">
            Interjúk
          </TabsTrigger>
        </TabsList>

        <TabsContent value="applications" className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
            <FilterChip
              label="Összes"
              count={counts.all}
              active={filter === "all"}
              onClick={() => setFilter("all")}
            />
            {STATUS_ORDER.map((s) => (
              <FilterChip
                key={s}
                label={STATUS_LABELS[s]}
                count={counts[s]}
                tone={s}
                active={filter === s}
                onClick={() => setFilter(s)}
              />
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="application-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Keresés név, email, telefon szerint…"
                className="pl-9"
              />
            </div>
          </div>

          {visible.length === 0 ? (
            <div
              data-testid="applications-empty"
              className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--sipx-elevated))] px-6 py-16 text-center"
            >
              <p className="text-sm font-medium">Nincs jelentkezés</p>
              <p className="max-w-sm text-xs text-muted-foreground">
                {applications.length === 0
                  ? "Ossza meg a jelentkezési linket — az új jelentkezések itt jelennek meg."
                  : "A jelenlegi szűrőre nincs találat."}
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
              <table className="w-full min-w-[640px] text-sm" data-testid="applications-table">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                    <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                      Jelölt
                    </th>
                    <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                      Kapcsolat
                    </th>
                    <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                      Vállalás
                    </th>
                    <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                      Beérkezett
                    </th>
                    <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                      Státusz
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((a) => (
                    <tr
                      key={a._id}
                      data-testid={`application-row-${a._id}`}
                      onClick={() => setSelected(a)}
                      className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                    >
                      <td className="h-14 px-4 align-middle">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {a.last_name} {a.first_name}
                          </p>
                          {a.onboarded_employee_id && (
                            <span
                              data-testid={`onboarded-badge-${a._id}`}
                              title="Onboarding már elindítva"
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300"
                            >
                              <IdCard className="h-3 w-3" strokeWidth={2} />
                              Onboardingelve
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {a.hospitality_experience ? "Vendéglátós tapasztalat" : "Nincs tapasztalat"}
                          {a.student_under_25 ? " · Diák (25 alatt)" : ""}
                        </p>
                      </td>
                      <td className="h-14 px-4 align-middle text-xs text-muted-foreground">
                        <p className="flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {a.email}
                        </p>
                        <p className="flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {a.phone}
                        </p>
                      </td>
                      <td className="h-14 px-4 align-middle">
                        <span className="rounded-full bg-foreground/[0.05] px-2 py-0.5 text-xs font-medium">
                          {a.weekly_commitment} nap / hét
                        </span>
                      </td>
                      <td className="h-14 px-4 align-middle text-xs text-muted-foreground">
                        {formatDateHu(a.created_at)}
                      </td>
                      <td
                        className="h-14 px-4 align-middle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select
                          value={a.status}
                          onValueChange={(v) => onStatusChange(a, v)}
                        >
                          <SelectTrigger
                            data-testid={`application-status-${a._id}`}
                            className={cn(
                              "h-7 w-[160px] border-0 text-xs font-medium",
                              STATUS_TONES[a.status]
                            )}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_ORDER.map((s) => (
                              <SelectItem key={s} value={s}>
                                {STATUS_LABELS[s]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="interviews">
          <PositionInterviewsTab
            positionId={id}
            onSelectApplication={(appId) => {
              const app = applications.find((a) => a._id === appId);
              if (app) setSelected(app);
            }}
          />
        </TabsContent>
      </Tabs>

      <ApplicationDetailDialog
        application={selected}
        onOpenChange={(o) => !o && setSelected(null)}
        onStatusChange={onStatusChange}
        onDelete={onDeleteApp}
        onApplicationChange={updateApplication}
      />
    </div>
  );
}

function FilterChip({ label, count, tone, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
        active
          ? tone
            ? STATUS_TONES[tone]
            : "bg-foreground text-background"
          : "text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground"
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          "rounded-full px-1.5 text-[10px]",
          active ? "bg-black/10 dark:bg-white/15" : "bg-foreground/[0.06]"
        )}
      >
        {count}
      </span>
    </button>
  );
}

function ApplicationDetailDialog({
  application,
  onOpenChange,
  onStatusChange,
  onDelete,
  onApplicationChange,
}) {
  if (!application) return null;
  const openCv = () => {
    api
      .get(`/applications/${application._id}/cv`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], {
          type:
            res.headers["content-type"] ||
            application.cv_content_type ||
            "application/pdf",
        });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) =>
        toast.error(
          formatApiError(e?.response?.data?.detail) ||
            "Nem sikerült megnyitni az önéletrajzot."
        )
      );
  };

  return (
    <Dialog open={!!application} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="application-detail-dialog"
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {application.last_name} {application.first_name}
          </DialogTitle>
          <DialogDescription>{application.position_name}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <InfoRow label="Email">{application.email}</InfoRow>
          <InfoRow label="Telefon">{application.phone}</InfoRow>
          <InfoRow label="Születési idő">
            {formatDateHu(application.birth_date)}
          </InfoRow>
          <InfoRow label="Heti vállalás">
            {application.weekly_commitment} nap
          </InfoRow>
          <InfoRow label="Vendéglátós tapasztalat">
            {application.hospitality_experience ? "Igen" : "Nem"}
          </InfoRow>
          <InfoRow label="25 év alatti diák">
            {application.student_under_25 ? "Igen" : "Nem"}
          </InfoRow>
          {application.social_link && (
            <InfoRow label="Social link" wide>
              <a
                href={application.social_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                {application.social_link}
              </a>
            </InfoRow>
          )}
          {application.about_me && (
            <InfoRow label="Magamról" wide>
              <p className="whitespace-pre-wrap">{application.about_me}</p>
            </InfoRow>
          )}
          {application.cv_url && (
            <InfoRow label="Önéletrajz" wide>
              <button
                onClick={openCv}
                className="inline-flex items-center gap-1.5 text-blue-600 hover:underline dark:text-blue-400"
              >
                <FileText className="h-3.5 w-3.5" />
                {application.cv_name || "Önéletrajz megnyitása"}
              </button>
            </InfoRow>
          )}
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <p className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
            Státusz módosítása
          </p>
          <Select
            value={application.status}
            onValueChange={(v) => onStatusChange(application, v)}
          >
            <SelectTrigger
              data-testid="application-detail-status"
              className={cn("h-9", STATUS_TONES[application.status])}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <OnboardingBlock
          application={application}
          onApplicationChange={onApplicationChange}
        />

        <ApplicationInterviews
          application={application}
          onApplicationChange={onApplicationChange}
        />

        <ApplicationTimeline
          application={application}
          onApplicationChange={onApplicationChange}
        />

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onDelete(application)}
            className="text-destructive hover:text-destructive"
            data-testid="delete-application"
          >
            <Trash2 className="h-4 w-4" /> Törlés
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Bezárás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, wide, children }) {
  return (
    <div className={wide ? "space-y-1 sm:col-span-2" : "space-y-1"}>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <div className="text-[13.5px]">{children}</div>
    </div>
  );
}

function formatDateTimeHu(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("hu-HU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
}

function ApplicationTimeline({ application, onApplicationChange }) {
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Merge notes + status history into a single chronological array (newest first).
  const events = useMemo(() => {
    const arr = [];
    (application.notes || []).forEach((n) => {
      arr.push({
        kind: "note",
        _id: n._id,
        at: n.created_at,
        actor: n.created_by_name,
        content: n.note,
      });
    });
    (application.interviews || []).forEach((iv) => {
      arr.push({
        kind: "interview",
        _id: `iv-${iv._id}`,
        at: iv.created_at || iv.scheduled_at,
        actor: iv.created_by_name,
        interview: iv,
      });
    });
    (application.status_history || []).forEach((h, i) => {
      arr.push({
        kind: "status",
        _id: `status-${i}-${h.changed_at}`,
        at: h.changed_at,
        actor: h.changed_by_name,
        status: h.status,
        content: h.note,
      });
    });
    return arr.sort((a, b) => new Date(b.at) - new Date(a.at));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application.notes, application.status_history, application.interviews]);

  const submitNote = async (e) => {
    e.preventDefault();
    const text = noteDraft.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data: newNote } = await api.post(
        `/applications/${application._id}/notes`,
        { note: text }
      );
      onApplicationChange(application._id, (a) => ({
        ...a,
        notes: [...(a.notes || []), newNote],
      }));
      setNoteDraft("");
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          "Nem sikerült menteni a bejegyzést."
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Törlöd ezt a bejegyzést?")) return;
    try {
      await api.delete(`/applications/${application._id}/notes/${noteId}`);
      onApplicationChange(application._id, (a) => ({
        ...a,
        notes: (a.notes || []).filter((n) => n._id !== noteId),
      }));
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          "Nem sikerült törölni a bejegyzést."
      );
    }
  };

  return (
    <section
      data-testid="application-timeline"
      className="space-y-3 rounded-md border border-border p-3"
    >
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Activity className="h-3.5 w-3.5" strokeWidth={2} />
        <p className="text-[10.5px] font-medium uppercase tracking-[0.16em]">
          Idővonal
        </p>
      </div>

      {/* Add note form */}
      <form onSubmit={submitNote} className="flex flex-col gap-2">
        <Textarea
          data-testid="timeline-note-input"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          rows={2}
          placeholder="Új információ a jelölthöz… (interjú visszajelzés, kapcsolatfelvétel részletei, megjegyzés)"
          className="min-h-[64px] resize-none text-sm"
          maxLength={4000}
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={saving || !noteDraft.trim()}
            data-testid="timeline-note-submit"
          >
            {saving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Mentés…
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" /> Hozzáadás
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Timeline list */}
      {events.length === 0 ? (
        <p className="rounded-md bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
          Még nincs bejegyzés az idővonalon.
        </p>
      ) : (
        <ol className="relative space-y-3 border-l border-border pl-5">
          {events.map((ev) => (
            <li
              key={ev._id}
              data-testid={`timeline-event-${ev._id}`}
              className="relative"
            >
              <span
                className={cn(
                  "absolute -left-[26px] top-1 grid h-5 w-5 place-items-center rounded-full ring-4 ring-[hsl(var(--sipx-elevated))]",
                  ev.kind === "note"
                    ? "bg-blue-500/15 text-blue-700 dark:text-blue-300"
                    : ev.kind === "interview"
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                    : STATUS_TONES[ev.status] ||
                        "bg-foreground/[0.06] text-muted-foreground"
                )}
              >
                {ev.kind === "note" ? (
                  <MessageSquare className="h-2.5 w-2.5" strokeWidth={2.5} />
                ) : ev.kind === "interview" ? (
                  <CalendarClock className="h-2.5 w-2.5" strokeWidth={2.5} />
                ) : (
                  <Activity className="h-2.5 w-2.5" strokeWidth={2.5} />
                )}
              </span>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
                    {ev.kind === "status" ? (
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[10.5px] font-medium",
                          STATUS_TONES[ev.status]
                        )}
                      >
                        {STATUS_LABELS[ev.status] || ev.status}
                      </span>
                    ) : ev.kind === "interview" ? (
                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10.5px] font-medium text-amber-700 dark:text-amber-300">
                        Interjú ütemezve
                      </span>
                    ) : (
                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                        Bejegyzés
                      </span>
                    )}
                    {ev.actor && (
                      <span className="text-[11px] text-muted-foreground">
                        · {ev.actor}
                      </span>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      · {formatDateTimeHu(ev.at)}
                    </span>
                  </div>
                  {ev.kind === "interview" ? (
                    <InterviewSummary iv={ev.interview} />
                  ) : (
                    ev.content && (
                      <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed">
                        {ev.content}
                      </p>
                    )
                  )}
                </div>
                {ev.kind === "note" && (
                  <button
                    type="button"
                    onClick={() => deleteNote(ev._id)}
                    data-testid={`timeline-delete-${ev._id}`}
                    aria-label="Bejegyzés törlése"
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}


/* -------------------- Interviews -------------------- */

function ApplicationInterviews({ application, onApplicationChange }) {
  const [open, setOpen] = useState(false);
  const list = (application.interviews || [])
    .slice()
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

  const onDelete = async (iv) => {
    if (!window.confirm("Törlöd ezt az interjú időpontot?")) return;
    try {
      await api.delete(`/applications/${application._id}/interviews/${iv._id}`);
      onApplicationChange(application._id, (a) => ({
        ...a,
        interviews: (a.interviews || []).filter((x) => x._id !== iv._id),
      }));
      toast.success("Interjú időpont törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült törölni az interjút."
      );
    }
  };

  const onOutcomeChange = async (iv, newOutcome) => {
    try {
      const { data } = await api.patch(
        `/applications/${application._id}/interviews/${iv._id}/outcome`,
        { outcome: newOutcome }
      );
      onApplicationChange(application._id, (a) => ({
        ...a,
        interviews: (a.interviews || []).map((x) =>
          x._id === iv._id ? { ...x, ...data } : x
        ),
      }));
      toast.success(
        newOutcome === "successful"
          ? "Sikeres interjúnak jelölve."
          : "Sikertelen interjúnak jelölve."
      );
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült frissíteni az eredményt."
      );
    }
  };

  return (
    <section
      data-testid="application-interviews"
      className="space-y-3 rounded-md border border-border p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <CalendarClock className="h-3.5 w-3.5" strokeWidth={2} />
          <p className="text-[10.5px] font-medium uppercase tracking-[0.16em]">
            Interjúk
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          onClick={() => setOpen(true)}
          data-testid="new-interview-button"
        >
          <Plus className="h-3.5 w-3.5" /> Új interjú
        </Button>
      </div>

      {list.length === 0 ? (
        <p
          data-testid="interviews-empty"
          className="rounded-md bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground"
        >
          Még nincs ütemezett interjú.
        </p>
      ) : (
        <ul className="space-y-2">
          {list.map((iv) => (
            <li
              key={iv._id}
              data-testid={`interview-${iv._id}`}
              className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium",
                      iv.mode === "online"
                        ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                        : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    )}
                  >
                    {iv.mode === "online" ? (
                      <>
                        <Video className="h-3 w-3" /> Online
                      </>
                    ) : (
                      <>
                        <MapPin className="h-3 w-3" /> Személyes
                      </>
                    )}
                  </span>
                  <span className="text-sm font-semibold tracking-tight">
                    {formatDateTimeHu(iv.scheduled_at)}
                  </span>
                </div>
                <InterviewSummary iv={iv} />
                <div className="pt-1">
                  <InterviewOutcomeButtons
                    interview={iv}
                    onChange={(out) => onOutcomeChange(iv, out)}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <InterviewFormDialog
        open={open}
        onOpenChange={setOpen}
        application={application}
        onCreated={(iv) =>
          onApplicationChange(application._id, (a) => ({
            ...a,
            interviews: [...(a.interviews || []), iv],
          }))
        }
      />
    </section>
  );
}

function InterviewSummary({ iv }) {
  return (
    <div className="space-y-0.5 text-xs text-muted-foreground">
      {iv.mode === "in_person" && iv.site_name && (
        <p className="flex items-center gap-1">
          <MapPin className="h-3 w-3" strokeWidth={1.75} />
          {iv.site_name}
        </p>
      )}
      {iv.mode === "online" && iv.meeting_link && (
        <p className="flex items-center gap-1">
          <LinkIcon className="h-3 w-3" strokeWidth={1.75} />
          <a
            href={iv.meeting_link}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-blue-600 hover:underline dark:text-blue-400"
          >
            {iv.meeting_link}
          </a>
        </p>
      )}
      {iv.responsible_name && (
        <p>Felelős: <span className="text-foreground">{iv.responsible_name}</span></p>
      )}
      {iv.notes && <p className="whitespace-pre-wrap">{iv.notes}</p>}
    </div>
  );
}

function InterviewFormDialog({ open, onOpenChange, application, onCreated }) {
  const [mode, setMode] = useState("online");
  const [siteId, setSiteId] = useState("");
  const [responsibleId, setResponsibleId] = useState("");
  const [scheduledAt, setScheduledAt] = useState(""); // ISO YYYY-MM-DDTHH:mm
  const [meetingLink, setMeetingLink] = useState("");
  const [notes, setNotes] = useState("");
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("online");
    setSiteId("");
    setResponsibleId("");
    setScheduledAt("");
    setMeetingLink("");
    setNotes("");
    setErr("");
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/sites"),
          api.get("/users"),
        ]);
        setSites(s.data);
        setUsers(u.data);
      } catch {
        // ignore
      }
    })();
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!scheduledAt) {
      setErr("Időpont megadása kötelező.");
      return;
    }
    if (!responsibleId) {
      setErr("Felelős kiválasztása kötelező.");
      return;
    }
    if (mode === "in_person" && !siteId) {
      setErr("Személyes interjúhoz válassz telephelyet.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        mode,
        scheduled_at: scheduledAt,
        responsible_id: responsibleId,
        site_id: mode === "in_person" ? siteId : null,
        meeting_link: mode === "online" ? meetingLink.trim() || null : null,
        notes: notes.trim() || null,
      };
      const { data } = await api.post(
        `/applications/${application._id}/interviews`,
        payload
      );
      onCreated(data);
      toast.success("Interjú időpont rögzítve.");
      onOpenChange(false);
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) ||
          "Nem sikerült rögzíteni az interjút."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="interview-form-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Új interjú időpont</DialogTitle>
          <DialogDescription>
            Tervezd meg az interjút — online vagy személyes módon.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <p className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Interjú típusa
            </p>
            <div className="inline-flex w-full rounded-xl border border-border bg-background p-1">
              {[
                { v: "online", label: "Online", icon: Video },
                { v: "in_person", label: "Személyes", icon: MapPin },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = mode === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`interview-mode-${opt.v}`}
                    onClick={() => setMode(opt.v)}
                    className={cn(
                      "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      active
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div data-testid="interview-scheduled-at" className="block space-y-1.5">
            <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Időpont *
            </span>
            <DateTimeInput
              value={scheduledAt}
              onChange={setScheduledAt}
            />
          </div>

          <label
            className="block space-y-1.5"
            data-testid="interview-responsible"
          >
            <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Felelős *
            </span>
            <Select value={responsibleId} onValueChange={setResponsibleId}>
              <SelectTrigger>
                <SelectValue placeholder="Válassz felelőst…" />
              </SelectTrigger>
              <SelectContent>
                {users.map((u) => (
                  <SelectItem key={u._id} value={u._id}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          {mode === "in_person" && (
            <label
              className="block space-y-1.5"
              data-testid="interview-site"
            >
              <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Telephely *
              </span>
              <Select value={siteId} onValueChange={setSiteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Válassz telephelyet…" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )}

          {mode === "online" && (
            <label
              className="block space-y-1.5"
              data-testid="interview-link"
            >
              <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Meeting link
              </span>
              <Input
                type="url"
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://meet.google.com/..."
              />
            </label>
          )}

          <label className="block space-y-1.5" data-testid="interview-notes">
            <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Megjegyzés
            </span>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Opcionális belső megjegyzés."
              className="resize-none"
            />
          </label>

          {err && (
            <div
              data-testid="interview-error"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
            >
              {err}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Mégse
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="interview-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : (
                "Rögzítés"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Position Interviews tab -------------------- */

function PositionInterviewsTab({ positionId, onSelectApplication }) {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | successful | unsuccessful

  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/positions/${positionId}/interviews`);
      setInterviews(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni az interjúkat."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  const counts = useMemo(() => {
    const c = { all: interviews.length, pending: 0, successful: 0, unsuccessful: 0 };
    interviews.forEach((iv) => {
      if (iv.outcome === "successful") c.successful += 1;
      else if (iv.outcome === "unsuccessful") c.unsuccessful += 1;
      else c.pending += 1;
    });
    return c;
  }, [interviews]);

  const visible = useMemo(() => {
    if (filter === "all") return interviews;
    if (filter === "pending") return interviews.filter((iv) => !iv.outcome);
    return interviews.filter((iv) => iv.outcome === filter);
  }, [interviews, filter]);

  const onOutcomeChange = async (iv, newOutcome) => {
    try {
      const { data } = await api.patch(
        `/applications/${iv.application_id}/interviews/${iv._id}/outcome`,
        { outcome: newOutcome }
      );
      setInterviews((prev) =>
        prev.map((x) =>
          x._id === iv._id && x.application_id === iv.application_id
            ? { ...x, ...data }
            : x
        )
      );
      toast.success(
        newOutcome === "successful"
          ? "Sikeres interjúnak jelölve."
          : "Sikertelen interjúnak jelölve."
      );
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült frissíteni az eredményt."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] px-6 py-14 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Interjúk betöltése…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <FilterChip
          label="Összes"
          count={counts.all}
          active={filter === "all"}
          onClick={() => setFilter("all")}
        />
        <FilterChip
          label="Folyamatban"
          count={counts.pending}
          active={filter === "pending"}
          onClick={() => setFilter("pending")}
        />
        <FilterChip
          label="Sikeres"
          count={counts.successful}
          active={filter === "successful"}
          onClick={() => setFilter("successful")}
        />
        <FilterChip
          label="Sikertelen"
          count={counts.unsuccessful}
          active={filter === "unsuccessful"}
          onClick={() => setFilter("unsuccessful")}
        />
      </div>

      {visible.length === 0 ? (
        <div
          data-testid="interviews-tab-empty"
          className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--sipx-elevated))] px-6 py-16 text-center"
        >
          <div className="grid h-12 w-12 place-items-center rounded-full border border-border bg-background text-muted-foreground/80">
            <CalendarClock className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium">
            {interviews.length === 0
              ? "Még nincs ütemezett interjú"
              : "Erre a szűrőre nincs találat"}
          </p>
          {interviews.length === 0 && (
            <p className="max-w-sm text-xs text-muted-foreground">
              A jelentkezések részleteinél tudsz új interjú időpontot rögzíteni.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
          <table className="w-full min-w-[640px] text-sm" data-testid="position-interviews-table">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  Jelölt
                </th>
                <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  Interjú időpont
                </th>
                <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  Mód / Helyszín
                </th>
                <th className="h-10 px-4 text-left text-[11px] font-medium uppercase tracking-[0.12em]">
                  Felelős
                </th>
                <th className="h-10 px-4 pr-4 text-right text-[11px] font-medium uppercase tracking-[0.12em]">
                  Eredmény
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((iv) => (
                <tr
                  key={`${iv.application_id}-${iv._id}`}
                  data-testid={`position-interview-${iv._id}`}
                  className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                >
                  <td className="h-16 px-4 align-middle">
                    <button
                      onClick={() => onSelectApplication(iv.application_id)}
                      className="text-left font-medium hover:underline"
                    >
                      {iv.candidate_last_name} {iv.candidate_first_name}
                    </button>
                    <p className="text-xs text-muted-foreground">
                      {iv.candidate_email}
                    </p>
                  </td>
                  <td className="h-16 px-4 align-middle">
                    <p className="font-medium">
                      {formatDateTimeHu(iv.scheduled_at)}
                    </p>
                  </td>
                  <td className="h-16 px-4 align-middle text-xs">
                    {iv.mode === "online" ? (
                      <p className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 font-medium text-violet-700 dark:text-violet-300">
                        <Video className="h-3 w-3" /> Online
                      </p>
                    ) : (
                      <p className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-300">
                        <MapPin className="h-3 w-3" /> Személyes
                      </p>
                    )}
                    {iv.mode === "in_person" && iv.site_name && (
                      <p className="mt-1 text-muted-foreground">{iv.site_name}</p>
                    )}
                    {iv.mode === "online" && iv.meeting_link && (
                      <a
                        href={iv.meeting_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 block truncate text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {iv.meeting_link}
                      </a>
                    )}
                  </td>
                  <td className="h-16 px-4 align-middle text-xs text-muted-foreground">
                    {iv.responsible_name || "—"}
                  </td>
                  <td className="h-16 px-4 pr-4 align-middle text-right">
                    <InterviewOutcomeButtons
                      interview={iv}
                      onChange={(out) => onOutcomeChange(iv, out)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Shared outcome toggle (Sikeres / Sikertelen) — once chosen it is final. */
function InterviewOutcomeButtons({ interview, onChange, compact = false }) {
  const o = interview.outcome;
  const locked = o === "successful" || o === "unsuccessful";
  const [pending, setPending] = useState(null); // "successful" | "unsuccessful" | null

  const requestChange = (next) => (e) => {
    e.stopPropagation();
    if (locked) return;
    setPending(next);
  };

  const confirm = () => {
    if (pending) onChange(pending);
    setPending(null);
  };

  return (
    <>
      <div
        className={cn(
          "inline-flex items-center gap-1.5",
          compact && "scale-95"
        )}
      >
        <button
          type="button"
          data-testid={`outcome-successful-${interview._id}`}
          onClick={requestChange("successful")}
          disabled={locked && o !== "successful"}
          aria-disabled={locked}
          title={
            locked
              ? "Az eredmény véglegesítve van."
              : "Sikeres interjú"
          }
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
            o === "successful"
              ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
              : locked
              ? "cursor-not-allowed border-border bg-background text-muted-foreground/40"
              : "border-border bg-background text-muted-foreground hover:border-emerald-500/40 hover:text-emerald-700 dark:hover:text-emerald-300"
          )}
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Sikeres
        </button>
        <button
          type="button"
          data-testid={`outcome-unsuccessful-${interview._id}`}
          onClick={requestChange("unsuccessful")}
          disabled={locked && o !== "unsuccessful"}
          aria-disabled={locked}
          title={
            locked
              ? "Az eredmény véglegesítve van."
              : "Sikertelen interjú"
          }
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
            o === "unsuccessful"
              ? "border-rose-500/40 bg-rose-500/15 text-rose-700 dark:text-rose-300"
              : locked
              ? "cursor-not-allowed border-border bg-background text-muted-foreground/40"
              : "border-border bg-background text-muted-foreground hover:border-rose-500/40 hover:text-rose-700 dark:hover:text-rose-300"
          )}
        >
          <XCircle className="h-3.5 w-3.5" />
          Sikertelen
        </button>
      </div>

      <AlertDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
      >
        <AlertDialogContent
          data-testid="outcome-confirm-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending === "successful"
                ? "Sikeres interjúnak jelölöd?"
                : "Sikertelen interjúnak jelölöd?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Az interjú eredménye véglegesítésre kerül, ez utólag{" "}
              <b>nem visszavonható</b> és nem módosítható. Biztosan így döntesz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="outcome-confirm-cancel">
              Mégse
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="outcome-confirm-accept"
              onClick={confirm}
              className={cn(
                pending === "successful"
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-rose-600 text-white hover:bg-rose-700"
              )}
            >
              {pending === "successful" ? "Igen, sikeres" : "Igen, sikertelen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { InterviewOutcomeButtons };


/* -------------------- Onboarding -------------------- */

const ONBOARDING_TYPES = [
  {
    v: "efo",
    label: "EFO",
    description: "Egyszerűsített foglalkoztatás · próbaidő nélkül",
  },
  {
    v: "student_coop",
    label: "Diákszövetkezet",
    description: "Diákszövetkezeten keresztül · próbaidő nélkül",
  },
  {
    v: "full_time",
    label: "Teljes munkaidős",
    description: "40 óra / hét · próbaidővel",
  },
  {
    v: "part_time",
    label: "Részmunkaidős",
    description: "20 / 30 óra / hét · próbaidővel",
  },
];

const FT_PT_MODE_OPTIONS = [
  { v: "employee", label: "Alkalmazotti" },
  { v: "partnership", label: "Társas Vállalkozó" },
];
const ONB_POSITIONS = [
  { v: "staff", label: "Staff" },
  { v: "shift_leader", label: "Shift Leader" },
  { v: "manager", label: "Manager" },
  { v: "area_manager", label: "Area Manager" },
];
const ONB_SCHEDULES = [
  { v: "five_day", label: "5 munkanapos" },
  { v: "variable", label: "Változó" },
];
const ONB_PT_HOURS = [
  { v: 20, label: "20 óra / hét" },
  { v: 30, label: "30 óra / hét" },
];

function OnboardingBlock({ application, onApplicationChange }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (application.status !== "hired" && !application.onboarded_employee_id) {
    return null;
  }
  const alreadyOnboarded = !!application.onboarded_employee_id;
  const hasSuccessfulInterview = (application.interviews || []).some(
    (iv) => iv?.outcome === "successful"
  );
  const canStart = alreadyOnboarded || hasSuccessfulInterview;

  return (
    <section
      data-testid="onboarding-block"
      className={cn(
        "space-y-3 rounded-md border p-3",
        alreadyOnboarded
          ? "border-emerald-500/30 bg-emerald-500/[0.04]"
          : canStart
          ? "border-foreground/15 bg-foreground/[0.02]"
          : "border-amber-500/30 bg-amber-500/[0.05]"
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "grid h-8 w-8 place-items-center rounded-lg",
              alreadyOnboarded
                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                : canStart
                ? "bg-foreground text-background"
                : "bg-amber-500/15 text-amber-700 dark:text-amber-300"
            )}
          >
            {alreadyOnboarded ? <IdCard className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
          </div>
          <div>
            <p className="text-sm font-semibold">
              {alreadyOnboarded
                ? "Onboarding elindítva"
                : canStart
                ? "Felvéve · Onboarding indítható"
                : "Sikeres interjú szükséges"}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {alreadyOnboarded
                ? "A munkavállalói profil létrejött, kiegészíthető a teljes adatlap."
                : canStart
                ? "Válaszd ki a munkaviszony típusát és töltsd ki a szükséges adatokat."
                : "Az onboarding csak sikeres interjú eredmény rögzítése után indítható."}
            </p>
          </div>
        </div>
        {alreadyOnboarded ? (
          <Button
            size="sm"
            variant="outline"
            data-testid="open-employee-profile"
            onClick={() => navigate(`/employees/${application.onboarded_employee_id}`)}
          >
            Profil megnyitása
          </Button>
        ) : (
          <Button
            size="sm"
            data-testid="start-onboarding"
            onClick={() => setOpen(true)}
            disabled={!canStart}
            title={
              canStart
                ? undefined
                : "Először rögzíts sikeres interjú eredményt."
            }
          >
            <UserPlus className="h-4 w-4" /> Onboarding kezdése
          </Button>
        )}
      </div>

      <OnboardingDialog
        open={open}
        onOpenChange={setOpen}
        application={application}
        onCompleted={(emp) => {
          onApplicationChange(application._id, (a) => ({
            ...a,
            onboarded_employee_id: emp.employee_id,
            onboarded_at: new Date().toISOString(),
          }));
          setOpen(false);
          navigate(`/employees/${emp.employee_id}`);
        }}
      />
    </section>
  );
}

function OnboardingDialog({ open, onOpenChange, application, onCompleted }) {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setType(null);
    setForm({});
    setErr("");
    Promise.all([api.get("/companies"), api.get("/sites")])
      .then(([c, s]) => {
        setCompanies(c.data);
        setSites(s.data);
      })
      .catch(() => {});
  }, [open]);

  const visibleSites = useMemo(() => {
    if (!form.company_id) return sites;
    return sites.filter((s) => s.company_id === form.company_id);
  }, [sites, form.company_id]);

  const requiresMonthlySalary = type === "full_time" || type === "part_time";
  const requiresHourly = type === "efo" || type === "student_coop";

  const submit = async () => {
    setErr("");
    // basic validation
    const req = ["company_id", "site_id", "position", "employment_start_date"];
    if (requiresHourly) req.push("gross_hourly_rate", "feor_number");
    if (requiresMonthlySalary) req.push("gross_monthly_salary", "probation_end_date");
    if (type === "full_time" || type === "part_time")
      req.push("employment_mode", "schedule_type");
    if (type === "part_time") req.push("weekly_hours");
    for (const k of req) {
      if (!form[k] && form[k] !== 0) {
        setErr("Minden kötelező mezőt ki kell tölteni.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { employment_type: type, ...form };
      ["gross_hourly_rate", "gross_monthly_salary", "weekly_hours"].forEach((k) => {
        if (payload[k] !== undefined && payload[k] !== null && payload[k] !== "") {
          payload[k] = Number(payload[k]);
        } else {
          delete payload[k];
        }
      });
      const { data } = await api.post(
        `/applications/${application._id}/onboard`,
        payload
      );
      toast.success("Munkavállalói profil létrehozva.");
      onCompleted(data);
    } catch (e) {
      setErr(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült létrehozni a profilt."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="onboarding-dialog"
        className="sm:max-w-xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            Onboarding · {application.last_name} {application.first_name}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Válaszd ki a munkaviszony típusát."
              : ONBOARDING_TYPES.find((t) => t.v === type)?.label}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {ONBOARDING_TYPES.map((t) => (
              <button
                key={t.v}
                type="button"
                data-testid={`onboarding-type-${t.v}`}
                onClick={() => {
                  setType(t.v);
                  // For full_time, weekly_hours is fixed.
                  if (t.v === "full_time") {
                    setForm((f) => ({ ...f, weekly_hours: 40 }));
                  }
                  setStep(2);
                }}
                className="flex flex-col items-start gap-1 rounded-xl border border-border bg-background p-4 text-left transition-colors hover:border-foreground/40 hover:bg-foreground/[0.03]"
              >
                <span className="text-base font-semibold">{t.label}</span>
                <span className="text-xs text-muted-foreground">{t.description}</span>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* FT/PT only: employment mode */}
            {(type === "full_time" || type === "part_time") && (
              <OnbField label="Foglalkoztatás módja *" wide testId="onb-mode">
                <Select
                  value={form.employment_mode || ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, employment_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Válassz…" />
                  </SelectTrigger>
                  <SelectContent>
                    {FT_PT_MODE_OPTIONS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </OnbField>
            )}

            <OnbField label="Munkáltató cég *" testId="onb-company">
              <Select
                value={form.company_id || ""}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, company_id: v, site_id: "" }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz céget…" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </OnbField>

            <OnbField label="Telephely *" testId="onb-site">
              <Select
                value={form.site_id || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, site_id: v }))}
                disabled={!form.company_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz telephelyet…" />
                </SelectTrigger>
                <SelectContent>
                  {visibleSites.map((s) => (
                    <SelectItem key={s._id} value={s._id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </OnbField>

            <OnbField label="Pozíció *" testId="onb-position">
              <Select
                value={form.position || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, position: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Válassz…" />
                </SelectTrigger>
                <SelectContent>
                  {ONB_POSITIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </OnbField>

            <OnbField label="Munkaviszony kezdete *" testId="onb-start">
              <DateInput
                value={form.employment_start_date || ""}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    employment_start_date: v,
                  }))
                }
              />
            </OnbField>

            {/* Probation end — only FT/PT */}
            {requiresMonthlySalary && (
              <OnbField label="Próbaidő vége *" testId="onb-probation">
                <DateInput
                  value={form.probation_end_date || ""}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, probation_end_date: v }))
                  }
                />
              </OnbField>
            )}

            {/* FEOR + hourly rate — EFO/student */}
            {requiresHourly && (
              <>
                <OnbField label="FEOR szám *" testId="onb-feor">
                  <Input
                    value={form.feor_number || ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, feor_number: e.target.value }))
                    }
                    placeholder="pl. 5113"
                  />
                </OnbField>
                <OnbField label="Bruttó órabér (Ft) *" testId="onb-hourly">
                  <NumericInput
                    value={form.gross_hourly_rate}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, gross_hourly_rate: e.target.value }))
                    }
                  />
                </OnbField>
              </>
            )}

            {/* Monthly salary — FT/PT */}
            {requiresMonthlySalary && (
              <OnbField label="Bruttó havi bér (Ft) *" testId="onb-monthly">
                <NumericInput
                  value={form.gross_monthly_salary}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, gross_monthly_salary: e.target.value }))
                  }
                />
              </OnbField>
            )}

            {/* Schedule type — FT/PT */}
            {(type === "full_time" || type === "part_time") && (
              <OnbField label="Beosztás rendje *" testId="onb-schedule">
                <Select
                  value={form.schedule_type || ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, schedule_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Válassz…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ONB_SCHEDULES.map((o) => (
                      <SelectItem key={o.v} value={o.v}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </OnbField>
            )}

            {/* Weekly hours — part-time only */}
            {type === "part_time" && (
              <OnbField label="Heti munkaidő *" testId="onb-weekly">
                <Select
                  value={form.weekly_hours ? String(form.weekly_hours) : ""}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, weekly_hours: Number(v) }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Válassz…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ONB_PT_HOURS.map((o) => (
                      <SelectItem key={o.v} value={String(o.v)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </OnbField>
            )}

            {type === "full_time" && (
              <OnbField label="Heti munkaidő" testId="onb-weekly-fixed">
                <div className="flex h-9 items-center rounded-md border border-border bg-muted/40 px-3 text-sm">
                  40 óra / hét (rögzített)
                </div>
              </OnbField>
            )}

            {err && (
              <div
                data-testid="onboarding-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive sm:col-span-2"
              >
                {err}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              data-testid="onboarding-back"
            >
              Vissza
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="onboarding-cancel"
          >
            Mégse
          </Button>
          {step === 2 && (
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="onboarding-confirm"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Létrehozás…
                </>
              ) : (
                "Profil létrehozása"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnbField({ label, children, testId, wide }) {
  return (
    <label
      className={cn("block space-y-1.5", wide && "sm:col-span-2")}
      data-testid={testId}
    >
      <span className="block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

